import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '../../contexts/ThemeContext';
import { getCardSx, tokens } from '../../theme/tokens';
import UnitPageShell from '../../components/shared/UnitPageShell';
import { useAuth } from '../../context/AuthContext';

type MissingRoomField = 'energy' | 'volume' | 'deltaT' | 'hours' | 'power';
type ThermalMode = 'heating' | 'cooling';
type GeometryMode = 'volume' | 'area_height';
type HeatingMissingField =
  | 'total'
  | 'transmission'
  | 'ventilation'
  | 'reheat'
  | 'uValue'
  | 'envelopeArea'
  | 'airChangeRate'
  | 'ventVolume'
  | 'deltaT';

type EstimatePreset = {
  label: string;
  value: number;
  note: string;
};

const normalizeNumber = (value: string): number | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number.parseFloat(trimmed.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

const formatNumber = (value: number, digits = 2): string =>
  new Intl.NumberFormat('de-DE', {
    minimumFractionDigits: Number.isInteger(value) ? 0 : Math.min(digits, 4),
    maximumFractionDigits: digits
  }).format(value);

const Page: React.FC = () => {
  const { darkMode } = useTheme();
  const { canViewEnergieRechner } = useAuth();

  const [missingField, setMissingField] = useState<MissingRoomField>('energy');
  const [thermalMode, setThermalMode] = useState<ThermalMode>('heating');
  const [geometryMode, setGeometryMode] = useState<GeometryMode>('area_height');
  const [area, setArea] = useState('80');
  const [height, setHeight] = useState('2.5');
  const [volume, setVolume] = useState('200');
  const [deltaT, setDeltaT] = useState('10');
  const [airDensity, setAirDensity] = useState('1.2');
  const [cp, setCp] = useState('1.005');
  const [safetyFactor, setSafetyFactor] = useState('1.0');
  const [energy, setEnergy] = useState('');
  const [hours, setHours] = useState('4');
  const [power, setPower] = useState('');

  const [heatingMissingField, setHeatingMissingField] = useState<HeatingMissingField>('total');
  const [heatDeltaT, setHeatDeltaT] = useState('20');
  const [uValue, setUValue] = useState('1.0');
  const [envelopeArea, setEnvelopeArea] = useState('50');
  const [airChangeRate, setAirChangeRate] = useState('0.5');
  const [ventVolume, setVentVolume] = useState('125');
  const [transmissionLoss, setTransmissionLoss] = useState('');
  const [ventilationLoss, setVentilationLoss] = useState('');
  const [reheatPower, setReheatPower] = useState('0');
  const [totalHeatLoad, setTotalHeatLoad] = useState('');

  const [estimateArea, setEstimateArea] = useState('20');
  const [estimatePreset, setEstimatePreset] = useState('80');

  const effectiveVolume = useMemo(() => {
    if (geometryMode === 'volume') return normalizeNumber(volume);
    const areaNum = normalizeNumber(area);
    const heightNum = normalizeNumber(height);
    if (areaNum === null || heightNum === null) return null;
    return areaNum * heightNum;
  }, [geometryMode, volume, area, height]);

  const energyResult = useMemo(() => {
    const volumeNum = effectiveVolume;
    const deltaNum = normalizeNumber(deltaT);
    const rhoNum = normalizeNumber(airDensity);
    const cpNum = normalizeNumber(cp);
    const sfNum = normalizeNumber(safetyFactor) ?? 1;
    const energyNum = normalizeNumber(energy);
    const hoursNum = normalizeNumber(hours);
    const powerNum = normalizeNumber(power);

    const calcEnergy = () => {
      if (volumeNum === null || deltaNum === null || rhoNum === null || cpNum === null) return null;
      const kJ = volumeNum * rhoNum * cpNum * Math.abs(deltaNum) * sfNum;
      return kJ / 3600;
    };

    switch (missingField) {
      case 'energy':
        return calcEnergy();
      case 'volume':
        if (energyNum === null || deltaNum === null || rhoNum === null || cpNum === null || sfNum === 0 || deltaNum === 0 || rhoNum === 0 || cpNum === 0) return null;
        return (energyNum * 3600) / (rhoNum * cpNum * Math.abs(deltaNum) * sfNum);
      case 'deltaT':
        if (energyNum === null || volumeNum === null || rhoNum === null || cpNum === null || sfNum === 0 || volumeNum === 0 || rhoNum === 0 || cpNum === 0) return null;
        return (energyNum * 3600) / (volumeNum * rhoNum * cpNum * sfNum);
      case 'hours':
        if (energyNum === null || powerNum === null || powerNum === 0) return null;
        return energyNum / powerNum;
      case 'power':
        if (energyNum === null || hoursNum === null || hoursNum === 0) return null;
        return energyNum / hoursNum;
      default:
        return null;
    }
  }, [effectiveVolume, deltaT, airDensity, cp, safetyFactor, energy, hours, power, missingField]);

  const calculationSteps = useMemo(() => {
    const volumeNum = effectiveVolume;
    const deltaNum = normalizeNumber(deltaT);
    const rhoNum = normalizeNumber(airDensity);
    const cpNum = normalizeNumber(cp);
    const sfNum = normalizeNumber(safetyFactor) ?? 1;
    if (volumeNum === null || deltaNum === null || rhoNum === null || cpNum === null) return [] as string[];
    const kJ = volumeNum * rhoNum * cpNum * Math.abs(deltaNum) * sfNum;
    const kWh = kJ / 3600;
    const lines = [
      `1) Masse Luft: m = Volumen * rho = ${formatNumber(volumeNum, 3)} * ${formatNumber(rhoNum, 3)} = ${formatNumber(volumeNum * rhoNum, 3)} kg`,
      `2) Temperaturarbeit: Q = m * cp * |DeltaT| * SF = ${formatNumber(volumeNum * rhoNum, 3)} * ${formatNumber(cpNum, 3)} * ${formatNumber(Math.abs(deltaNum), 3)} * ${formatNumber(sfNum, 3)} = ${formatNumber(kJ, 3)} kJ`,
      `3) Umrechnung: E = Q / 3600 = ${formatNumber(kWh, 3)} kWh`
    ];
    const hoursNum = normalizeNumber(hours);
    if (hoursNum !== null && hoursNum > 0) {
      lines.push(`4) Mittlere Leistung: P = E / h = ${formatNumber(kWh, 3)} / ${formatNumber(hoursNum, 3)} = ${formatNumber(kWh / hoursNum, 3)} kW`);
    }
    return lines;
  }, [effectiveVolume, deltaT, airDensity, cp, safetyFactor, hours]);

  const heatingLoadResult = useMemo(() => {
    const deltaNum = normalizeNumber(heatDeltaT);
    const uNum = normalizeNumber(uValue);
    const areaNum = normalizeNumber(envelopeArea);
    const nNum = normalizeNumber(airChangeRate);
    const volNum = normalizeNumber(ventVolume);
    const transNum = normalizeNumber(transmissionLoss);
    const ventNum = normalizeNumber(ventilationLoss);
    const reheatNum = normalizeNumber(reheatPower) ?? 0;
    const totalNum = normalizeNumber(totalHeatLoad);

    const calcTransmission = () => {
      if (uNum === null || areaNum === null || deltaNum === null) return null;
      if (areaNum === 0 || deltaNum === 0) return null;
      return uNum * areaNum * deltaNum;
    };

    const calcVentilation = () => {
      if (nNum === null || volNum === null || deltaNum === null) return null;
      if (volNum === 0 || deltaNum === 0) return null;
      return 0.33 * nNum * volNum * deltaNum;
    };

    const resolveTransmission = () => {
      if (transNum !== null) return transNum;
      if (totalNum !== null && ventNum !== null) return totalNum - ventNum - reheatNum;
      return calcTransmission();
    };

    const resolveVentilation = () => {
      if (ventNum !== null) return ventNum;
      if (totalNum !== null && transNum !== null) return totalNum - transNum - reheatNum;
      return calcVentilation();
    };

    const transResolved = resolveTransmission();
    const ventResolved = resolveVentilation();

    switch (heatingMissingField) {
      case 'total': {
        if (transResolved === null || ventResolved === null) return null;
        return transResolved + ventResolved + reheatNum;
      }
      case 'transmission': {
        if (totalNum !== null && ventResolved !== null) return totalNum - ventResolved - reheatNum;
        return calcTransmission();
      }
      case 'ventilation': {
        if (totalNum !== null && transResolved !== null) return totalNum - transResolved - reheatNum;
        return calcVentilation();
      }
      case 'reheat': {
        if (totalNum === null || transResolved === null || ventResolved === null) return null;
        return totalNum - transResolved - ventResolved;
      }
      case 'uValue': {
        if (transResolved === null || areaNum === null || deltaNum === null) return null;
        if (areaNum === 0 || deltaNum === 0) return null;
        return transResolved / (areaNum * deltaNum);
      }
      case 'envelopeArea': {
        if (transResolved === null || uNum === null || deltaNum === null) return null;
        if (uNum === 0 || deltaNum === 0) return null;
        return transResolved / (uNum * deltaNum);
      }
      case 'airChangeRate': {
        if (ventResolved === null || volNum === null || deltaNum === null) return null;
        if (volNum === 0 || deltaNum === 0) return null;
        return ventResolved / (0.33 * volNum * deltaNum);
      }
      case 'ventVolume': {
        if (ventResolved === null || nNum === null || deltaNum === null) return null;
        if (nNum === 0 || deltaNum === 0) return null;
        return ventResolved / (0.33 * nNum * deltaNum);
      }
      case 'deltaT': {
        if (transResolved !== null && uNum !== null && areaNum !== null && uNum !== 0 && areaNum !== 0) {
          return transResolved / (uNum * areaNum);
        }
        if (ventResolved !== null && nNum !== null && volNum !== null && nNum !== 0 && volNum !== 0) {
          return ventResolved / (0.33 * nNum * volNum);
        }
        return null;
      }
      default:
        return null;
    }
  }, [
    heatDeltaT,
    uValue,
    envelopeArea,
    airChangeRate,
    ventVolume,
    transmissionLoss,
    ventilationLoss,
    reheatPower,
    totalHeatLoad,
    heatingMissingField
  ]);

  const heatingLoadSteps = useMemo(() => {
    const deltaNum = normalizeNumber(heatDeltaT);
    const uNum = normalizeNumber(uValue);
    const areaNum = normalizeNumber(envelopeArea);
    const nNum = normalizeNumber(airChangeRate);
    const volNum = normalizeNumber(ventVolume);
    if (deltaNum === null) return [] as string[];

    const lines: string[] = [];
    if (uNum !== null && areaNum !== null) {
      const transCalc = uNum * areaNum * deltaNum;
      lines.push(`1) Transmission: Phi_T = U * A * DeltaT = ${formatNumber(uNum, 3)} * ${formatNumber(areaNum, 3)} * ${formatNumber(deltaNum, 3)} = ${formatNumber(transCalc, 2)} W`);
    }
    if (nNum !== null && volNum !== null) {
      const ventCalc = 0.33 * nNum * volNum * deltaNum;
      lines.push(`2) Lueftung: Phi_V = 0.33 * n * V * DeltaT = 0.33 * ${formatNumber(nNum, 3)} * ${formatNumber(volNum, 3)} * ${formatNumber(deltaNum, 3)} = ${formatNumber(ventCalc, 2)} W`);
    }
    if (lines.length > 1 && heatingLoadResult !== null) {
      lines.push(`3) Heizlast: Phi_H = Phi_T + Phi_V + Phi_A = ${formatNumber(heatingLoadResult, 2)} W`);
    }
    return lines;
  }, [heatDeltaT, uValue, envelopeArea, airChangeRate, ventVolume, heatingLoadResult]);

  const estimatePresets: EstimatePreset[] = [
    { label: 'Passivhaus / KfW 40', value: 10, note: 'Sehr gut gedaemmt, kontrollierte Lueftung' },
    { label: 'Neubau (ab 2016)', value: 35, note: 'EnEV/GEG Standard' },
    { label: 'Sanierter Altbau (80er/90er)', value: 70, note: 'Teilweise gedaemmt, neue Fenster' },
    { label: 'Altbau (unsaniert)', value: 120, note: 'Hohe Verluste, wenig Daemmung' }
  ];

  const estimateResult = useMemo(() => {
    const areaNum = normalizeNumber(estimateArea);
    const specNum = normalizeNumber(estimatePreset);
    if (areaNum === null || specNum === null) return null;
    return areaNum * specNum;
  }, [estimateArea, estimatePreset]);

  const displayLabel: Record<MissingRoomField, string> = {
    energy: 'Energie (kWh)',
    volume: 'Volumen (m3)',
    deltaT: 'Delta T (K)',
    hours: 'Zeit (h)',
    power: 'Leistung (kW)'
  };

  if (!canViewEnergieRechner) {
    return (
      <UnitPageShell title="Energie Rechner">
        <Box sx={{ p: 3 }}>
          <Alert severity="error">Keine Berechtigung fuer diese Seite.</Alert>
        </Box>
      </UnitPageShell>
    );
  }

  return (
    <UnitPageShell title="M99 U11 - Raum Energie Rechner">
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1.1fr) minmax(0, 0.9fr)' } }}>
        <Stack spacing={3}>
          <Card sx={getCardSx(darkMode)}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="h5" fontWeight={700}>Raumenergie Rechner</Typography>
                    <Tooltip title="Berechnet Heiz- und Kuehlenergie eines Raumvolumens inkl. optionalem Sicherheitsfaktor.">
                      <IconButton size="small" aria-label="Info Raumenergie"><InfoOutlinedIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Berechnet den Energiebedarf fuer Heizen oder Kuehlen. Rueckwaerts-Berechnung fuer Volumen, Delta T, Leistung oder Zeit.
                  </Typography>
                </Stack>

                <FormControl fullWidth>
                  <InputLabel id="thermal-mode">Betriebsart</InputLabel>
                  <Select
                    labelId="thermal-mode"
                    label="Betriebsart"
                    value={thermalMode}
                    onChange={(e) => setThermalMode(e.target.value as ThermalMode)}
                  >
                    <MenuItem value="heating">Heizen</MenuItem>
                    <MenuItem value="cooling">Kuehlen</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="geometry-mode">Raumangabe</InputLabel>
                  <Select
                    labelId="geometry-mode"
                    label="Raumangabe"
                    value={geometryMode}
                    onChange={(e) => setGeometryMode(e.target.value as GeometryMode)}
                  >
                    <MenuItem value="area_height">Flaeche + Hoehe</MenuItem>
                    <MenuItem value="volume">Direktes Volumen</MenuItem>
                  </Select>
                </FormControl>

                <FormControl fullWidth>
                  <InputLabel id="missing-field">Zielgroesse</InputLabel>
                  <Select
                    labelId="missing-field"
                    label="Zielgroesse"
                    value={missingField}
                    onChange={(e) => setMissingField(e.target.value as MissingRoomField)}
                  >
                    <MenuItem value="energy">Energie (kWh)</MenuItem>
                    <MenuItem value="volume">Volumen (m3)</MenuItem>
                    <MenuItem value="deltaT">Delta T (K)</MenuItem>
                    <MenuItem value="hours">Zeit (h)</MenuItem>
                    <MenuItem value="power">Leistung (kW)</MenuItem>
                  </Select>
                </FormControl>

                {geometryMode === 'volume' ? (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Volumen (m3)"
                      helperText="Raumvolumen fuer die Energiebilanz"
                      value={volume}
                      onChange={(e) => setVolume(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Delta T (K)"
                      helperText="Innen - Aussen (z.B. 20 - (-5) = 25 K)"
                      value={deltaT}
                      onChange={(e) => setDeltaT(e.target.value)}
                      fullWidth
                    />
                  </Stack>
                ) : (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                      label="Flaeche (m2)"
                      helperText="Bodenflaeche des Raums"
                      value={area}
                      onChange={(e) => setArea(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Raumhoehe (m)"
                      helperText="Lichte Hoehe (z.B. 2.5 m)"
                      value={height}
                      onChange={(e) => setHeight(e.target.value)}
                      fullWidth
                    />
                    <TextField
                      label="Delta T (K)"
                      helperText="Temperaturdifferenz innen/aussen"
                      value={deltaT}
                      onChange={(e) => setDeltaT(e.target.value)}
                      fullWidth
                    />
                  </Stack>
                )}

                {geometryMode === 'area_height' && (
                  <Typography variant="body2" color="text.secondary">
                    Effektives Volumen: {effectiveVolume === null ? '-' : `${formatNumber(effectiveVolume, 3)} m3`} (V = A * h)
                  </Typography>
                )}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Luftdichte rho (kg/m3)"
                    helperText="Normwert 1.2 bei 20 C"
                    value={airDensity}
                    onChange={(e) => setAirDensity(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="cp (kJ/kgK)"
                    helperText="Spezifische Waermekapazitaet"
                    value={cp}
                    onChange={(e) => setCp(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Sicherheitsfaktor"
                    helperText="z.B. 1.0 bis 1.3 fuer Reserve"
                    value={safetyFactor}
                    onChange={(e) => setSafetyFactor(e.target.value)}
                    fullWidth
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField label="Energie (kWh)" value={energy} onChange={(e) => setEnergy(e.target.value)} fullWidth />
                  <TextField label="Zeit (h)" value={hours} onChange={(e) => setHours(e.target.value)} fullWidth />
                  <TextField label="Leistung (kW)" value={power} onChange={(e) => setPower(e.target.value)} fullWidth />
                </Stack>

                {calculationSteps.length > 0 && (
                  <Box
                    sx={{
                      borderRadius: 2,
                      p: 2,
                      background: darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt,
                      border: `1px solid ${darkMode ? tokens.colors.dark.border : tokens.colors.light.border}`
                    }}
                  >
                    <Stack spacing={1}>
                      <Typography variant="subtitle2" color="text.secondary">Rechenweg</Typography>
                      {calculationSteps.map((line) => (
                        <Typography key={line} variant="body2" color="text.secondary">{line}</Typography>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={getCardSx(darkMode)}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography variant="h5" fontWeight={700}>Heizlast nach DIN EN 12831 (vereinfacht)</Typography>
                    <Tooltip title="Summiert Transmission, Lueftung und ggf. Aufheizleistung zur Heizlast in Watt.">
                      <IconButton size="small" aria-label="Info Heizlast"><InfoOutlinedIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  </Stack>
                  <Typography variant="body2" color="text.secondary">
                    Die Heizlast ist die maximale Leistung, um einen Raum bei Norm-Aussentemperatur zu halten. Ergebnis in Watt.
                  </Typography>
                </Stack>

                <FormControl fullWidth>
                  <InputLabel id="heating-missing-field">Zielgroesse</InputLabel>
                  <Select
                    labelId="heating-missing-field"
                    label="Zielgroesse"
                    value={heatingMissingField}
                    onChange={(e) => setHeatingMissingField(e.target.value as HeatingMissingField)}
                  >
                    <MenuItem value="total">Heizlast gesamt (W)</MenuItem>
                    <MenuItem value="transmission">Transmission (W)</MenuItem>
                    <MenuItem value="ventilation">Lueftung (W)</MenuItem>
                    <MenuItem value="reheat">Aufheizleistung (W)</MenuItem>
                    <MenuItem value="uValue">U-Wert (W/m2K)</MenuItem>
                    <MenuItem value="envelopeArea">Flaeche A (m2)</MenuItem>
                    <MenuItem value="airChangeRate">Luftwechsel n (1/h)</MenuItem>
                    <MenuItem value="ventVolume">Lueftungsvolumen V (m3)</MenuItem>
                    <MenuItem value="deltaT">Delta T (K)</MenuItem>
                  </Select>
                </FormControl>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Delta T (K)"
                    helperText="Norm-Innentemp minus Norm-Aussentemp"
                    value={heatDeltaT}
                    onChange={(e) => setHeatDeltaT(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="U-Wert (W/m2K)"
                    helperText="Daemmstandard der Huelle"
                    value={uValue}
                    onChange={(e) => setUValue(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Flaeche A (m2)"
                    helperText="Aussenflaechen (Wand, Dach, Fenster)"
                    value={envelopeArea}
                    onChange={(e) => setEnvelopeArea(e.target.value)}
                    fullWidth
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Luftwechsel n (1/h)"
                    helperText="z.B. 0.3 bis 0.7 bei Wohnraeumen"
                    value={airChangeRate}
                    onChange={(e) => setAirChangeRate(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Lueftungsvolumen V (m3)"
                    helperText="Raumvolumen fuer Lueftung"
                    value={ventVolume}
                    onChange={(e) => setVentVolume(e.target.value)}
                    fullWidth
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Transmission (W)"
                    helperText="Phi_T = U * A * DeltaT (oder aus Heizlast ableiten)"
                    value={transmissionLoss}
                    onChange={(e) => setTransmissionLoss(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Lueftung (W)"
                    helperText="Phi_V = 0.33 * n * V * DeltaT (oder aus Heizlast ableiten)"
                    value={ventilationLoss}
                    onChange={(e) => setVentilationLoss(e.target.value)}
                    fullWidth
                  />
                  <TextField
                    label="Aufheizleistung (W)"
                    helperText="Optional fuer schnelle Wiederaufheizung"
                    value={reheatPower}
                    onChange={(e) => setReheatPower(e.target.value)}
                    fullWidth
                  />
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Heizlast gesamt (W)"
                    helperText="Phi_H = Phi_T + Phi_V + Phi_A"
                    value={totalHeatLoad}
                    onChange={(e) => setTotalHeatLoad(e.target.value)}
                    fullWidth
                  />
                  <Box
                    sx={{
                      borderRadius: 2,
                      p: 2,
                      flex: 1,
                      background: darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt,
                      border: `1px solid ${darkMode ? tokens.colors.dark.border : tokens.colors.light.border}`
                    }}
                  >
                    <Typography variant="subtitle2" color="text.secondary">Ergebnis (DIN 12831)</Typography>
                    <Typography variant="h5" fontWeight={700} color={tokens.colors.primary}>
                      {heatingLoadResult === null ? '-' : `${formatNumber(heatingLoadResult, 2)} W`}
                    </Typography>
                    {heatingLoadSteps.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {heatingLoadSteps.join(' | ')}
                      </Typography>
                    )}
                  </Box>
                </Stack>

                <Button
                  variant="outlined"
                  onClick={() => {
                    if (heatingLoadResult === null) return;
                    const value = formatNumber(heatingLoadResult, 2);
                    if (heatingMissingField === 'total') setTotalHeatLoad(value);
                    if (heatingMissingField === 'transmission') setTransmissionLoss(value);
                    if (heatingMissingField === 'ventilation') setVentilationLoss(value);
                    if (heatingMissingField === 'reheat') setReheatPower(value);
                    if (heatingMissingField === 'uValue') setUValue(value);
                    if (heatingMissingField === 'envelopeArea') setEnvelopeArea(value);
                    if (heatingMissingField === 'airChangeRate') setAirChangeRate(value);
                    if (heatingMissingField === 'ventVolume') setVentVolume(value);
                    if (heatingMissingField === 'deltaT') setHeatDeltaT(value);
                  }}
                >
                  Ergebnis uebernehmen
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={getCardSx(darkMode)}>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={700}>Ueberschlaegige Heizlast (W/m2)</Typography>
                <Typography variant="body2" color="text.secondary">
                  Schnellabschaetzung ueber spezifische Heizlasten pro Quadratmeter. Werte sind Richtwerte.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <TextField
                    label="Flaeche (m2)"
                    helperText="Wohn- oder Nutzflaeche"
                    value={estimateArea}
                    onChange={(e) => setEstimateArea(e.target.value)}
                    fullWidth
                  />
                  <FormControl fullWidth>
                    <InputLabel id="estimate-preset">Gebaeudetyp</InputLabel>
                    <Select
                      labelId="estimate-preset"
                      label="Gebaeudetyp"
                      value={estimatePreset}
                      onChange={(e) => setEstimatePreset(e.target.value)}
                    >
                      {estimatePresets.map((preset) => (
                        <MenuItem key={preset.label} value={`${preset.value}`}>
                          {preset.label} ({preset.value} W/m2)
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2,
                    background: darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt,
                    border: `1px solid ${darkMode ? tokens.colors.dark.border : tokens.colors.light.border}`
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">Ergebnis</Typography>
                  <Typography variant="h5" fontWeight={700} color={tokens.colors.primary}>
                    {estimateResult === null ? '-' : `${formatNumber(estimateResult, 1)} W`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Formel: Heizlast = Flaeche * spezifische Heizlast
                  </Typography>
                </Box>
                <Divider />
                <Stack spacing={0.75}>
                  {estimatePresets.map((preset) => (
                    <Typography key={preset.label} variant="body2" color="text.secondary">
                      {preset.label}: {preset.value} W/m2 - {preset.note}
                    </Typography>
                  ))}
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>

        <Stack spacing={3}>
          <Card sx={getCardSx(darkMode)}>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={700}>Ergebnis</Typography>
                <Box
                  sx={{
                    borderRadius: 2,
                    p: 2,
                    background: darkMode ? tokens.colors.dark.surfaceAlt : tokens.colors.light.surfaceAlt,
                    border: `1px solid ${darkMode ? tokens.colors.dark.border : tokens.colors.light.border}`
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">{displayLabel[missingField]}</Typography>
                  <Typography variant="h4" fontWeight={700} color={tokens.colors.primary}>
                    {energyResult === null ? '-' : formatNumber(energyResult)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Betriebsart: {thermalMode === 'heating' ? 'Heizen' : 'Kuehlen'} • Volumenbasis: {effectiveVolume === null ? '-' : `${formatNumber(effectiveVolume, 2)} m3`}
                  </Typography>
                </Box>
                <Button variant="outlined" onClick={() => {
                  if (energyResult === null) return;
                  const value = formatNumber(energyResult);
                  if (missingField === 'energy') setEnergy(value);
                  if (missingField === 'volume') setVolume(value);
                  if (missingField === 'deltaT') setDeltaT(value);
                  if (missingField === 'hours') setHours(value);
                  if (missingField === 'power') setPower(value);
                }}>
                  Ergebnis uebernehmen
                </Button>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={getCardSx(darkMode)}>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6" fontWeight={700}>Vorgaben & Einflussfaktoren</Typography>
                <Typography variant="body2" color="text.secondary">
                  DIN EN 12831 betrachtet Transmission, Lueftung und Aufheizleistung. Alle Werte beziehen sich auf die Norm-Aussentemperatur.
                </Typography>
                <Divider />
                <Stack spacing={0.75}>
                  <Typography variant="body2" color="text.secondary">Formeln: Phi_T = U * A * DeltaT, Phi_V = 0.33 * n * V * DeltaT, Phi_H = Phi_T + Phi_V + Phi_A.</Typography>
                  <Typography variant="body2" color="text.secondary">Norm-Aussentemperatur: typ. -12 bis -16 C (regional unterschiedlich).</Typography>
                  <Typography variant="body2" color="text.secondary">Raumtemperaturen: Wohnen 20 C, Bad 22-24 C, Schlafen 18 C.</Typography>
                  <Typography variant="body2" color="text.secondary">U-Werte: Neubau 0.2-0.4 W/m2K, Altbau unsaniert 0.8-1.4 W/m2K.</Typography>
                  <Typography variant="body2" color="text.secondary">Luftwechsel n: 0.3-0.7 1/h (Nutzung und Lueftungssystem beachten).</Typography>
                  <Typography variant="body2" color="text.secondary">Aufheizleistung: optional, wenn der Raum nach Absenkung schnell aufgeheizt werden soll.</Typography>
                  <Typography variant="body2" color="text.secondary">Berechnung in allen Richtungen: Zielgroesse waehlen und die restlichen Felder fuellen.</Typography>
                </Stack>
                <Divider />
                <Typography variant="h6" fontWeight={700}>So verwenden</Typography>
                <Stack spacing={0.75}>
                  <Typography variant="body2" color="text.secondary">1) Zielgroesse (z.B. Heizlast, U-Wert, n, V oder DeltaT) auswaehlen.</Typography>
                  <Typography variant="body2" color="text.secondary">2) Passende Normwerte fuer Aussen-/Raumtemperatur festlegen.</Typography>
                  <Typography variant="body2" color="text.secondary">3) Alle bekannten Groessen eingeben (U, A, n, V, Phi_T/Phi_V/Phi_A).</Typography>
                  <Typography variant="body2" color="text.secondary">4) Ergebnis uebernehmen und ggf. Rueckrechnung pruefen.</Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>
    </UnitPageShell>
  );
};

export default Page;
